import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, MapPin, Users, Building2, X, RefreshCw } from "lucide-react";

const RegionsManagement = () => {
  const [regions, setRegions] = useState([]);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    manager_id: "",
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [r, u, b] = await Promise.all([
        axios.get(`${API}/regions`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/branches`),
      ]);
      setRegions(r.data.regions || []);
      setUsers(u.data.users || u.data || []);
      setBranches(b.data.branches || b.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load regions");
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setFormData({ name: "", code: "", description: "", manager_id: "" });
    setShowForm(true);
  };

  const openEdit = (r) => {
    setEditing(r.region_id);
    setFormData({
      name: r.name,
      code: r.code,
      description: r.description || "",
      manager_id: r.manager_id || "",
    });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.code) {
      toast.error("Name and code are required");
      return;
    }
    try {
      const payload = { ...formData, manager_id: formData.manager_id || null };
      if (editing) {
        await axios.put(`${API}/regions/${editing}`, payload);
        toast.success("Region updated");
      } else {
        await axios.post(`${API}/regions`, payload);
        toast.success("Region created");
      }
      setShowForm(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save region");
    }
  };

  const handleDelete = async (region) => {
    if (!window.confirm(`Delete region "${region.name}"? Branches will be unassigned.`)) return;
    try {
      await axios.delete(`${API}/regions/${region.region_id}`);
      toast.success("Region deleted");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const handleAssignBranch = async (regionId, branchId) => {
    try {
      await axios.post(`${API}/regions/${regionId}/branches/${branchId}`);
      toast.success("Branch assigned");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to assign");
    }
  };

  const handleRemoveBranch = async (regionId, branchId) => {
    try {
      await axios.delete(`${API}/regions/${regionId}/branches/${branchId}`);
      toast.success("Branch removed from region");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  const candidateManagers = users.filter((u) =>
    ["regional_manager", "admin", "branch_manager", "agent"].includes(u.role)
  );

  const unassignedBranches = branches.filter((b) => !b.region_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="regions-management">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Regions</h1>
          <p className="text-muted-foreground text-sm">
            Group branches into regions. Each region has a Regional Manager who reports to the General Manager.
          </p>
        </div>
        <Button onClick={openNew} data-testid="new-region-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Region
        </Button>
      </div>

      {regions.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            No regions yet. Click "New Region" to create one.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {regions.map((r) => (
            <Card key={r.region_id} data-testid={`region-card-${r.region_id}`}>
              <CardHeader className="flex flex-row items-start justify-between gap-2">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />
                    {r.name}
                    <span className="text-xs font-normal text-muted-foreground">({r.code})</span>
                  </CardTitle>
                  {r.description && <p className="text-sm text-muted-foreground mt-1">{r.description}</p>}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`edit-${r.region_id}`}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(r)} data-testid={`delete-${r.region_id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Regional Manager:</span>
                  <span className="font-medium">
                    {r.manager?.name || <em className="text-muted-foreground">unassigned</em>}
                  </span>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{r.branch_count} branch{r.branch_count !== 1 ? "es" : ""}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(r.branches || []).map((b) => (
                      <span
                        key={b.branch_id}
                        className="inline-flex items-center gap-1 bg-gray-100 rounded px-2 py-1 text-xs"
                      >
                        {b.name} ({b.code})
                        <button
                          onClick={() => handleRemoveBranch(r.region_id, b.branch_id)}
                          className="text-red-500 hover:text-red-700"
                          title="Remove from region"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  {unassignedBranches.length > 0 && (
                    <select
                      className="mt-2 px-2 py-1 text-sm border rounded"
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAssignBranch(r.region_id, e.target.value);
                          e.target.value = "";
                        }
                      }}
                      defaultValue=""
                      data-testid={`assign-branch-${r.region_id}`}
                    >
                      <option value="">+ Assign a branch...</option>
                      {unassignedBranches.map((b) => (
                        <option key={b.branch_id} value={b.branch_id}>
                          {b.name} ({b.code})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>{editing ? "Edit Region" : "New Region"}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Copperbelt, Lusaka, Southern"
                  data-testid="region-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Code *</label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. CPB, LUS, STH"
                  maxLength={6}
                  data-testid="region-code-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional notes about this region"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Regional Manager</label>
                <select
                  value={formData.manager_id}
                  onChange={(e) => setFormData({ ...formData, manager_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                  data-testid="region-manager-select"
                >
                  <option value="">— None / Assign later —</option>
                  {candidateManagers.map((u) => (
                    <option key={u.user_id} value={u.user_id}>
                      {u.name} ({u.email}) · {u.role}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Selecting a user will set their role to Regional Manager.
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSubmit} data-testid="save-region-btn">
                  {editing ? "Save" : "Create Region"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default RegionsManagement;
